import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, assetUrl, downloadUrl, ASSET_BASE } from "@/lib/api";
import { ARTICLE_TYPES, SPECIALTIES, ACADEMIC_DEGREES, DEPARTMENTS } from "@/lib/constants";
import { Plus, Trash2, Eye, Save, FileSignature, Upload, Image as ImageIcon, Download, RefreshCw, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import ComboTextField from "@/components/ComboTextField";
import DatePickerField from "@/components/DatePickerField";
import { buildCitation, parsePastedTable } from "@/lib/pdfHelpers";

const blankAuthor = () => ({
  first_name: "", middle_name: "", last_name: "", second_last_name: "",
  academic_degree: "", email: "", orcid: "", affiliation: "", department: "",
  institution: "", city: "", country: "", address: "", order: 1, corresponding: false,
  photo_url: "",
});

const defaultArticle = () => ({
  title: "", short_title: "", running_title: "", article_type: "Original Research",
  main_specialty: "", volume: "", issue: "", year: String(new Date().getFullYear()),
  pages: "", article_number: "", doi: "", doi_url: "",
  received_date: "", accepted_date: "", published_date: "",
  structured_abstract: true,
  abstract: "", abstract_background: "", abstract_methods: "", abstract_results: "", abstract_conclusions: "",
  keywords: [],
  authors: [{ ...blankAuthor(), corresponding: true }],
  introduction: "", methods: "", results: "", case_presentation: "", surgical_technique: "", discussion: "", conclusion: "",
  acknowledgments: "", funding: "", conflicts_of_interest: "",
  ethics_approval: "", informed_consent: "", data_availability: "", author_contributions: "",
  references: [],
  custom_sections: [],
  tables: [],
  figures: [],
  visual_abstract_url: "",
  how_to_cite: "",
  license: "CC BY-NC 4.0",
  featured: false, published: false, hidden: false,
  issue_id: "",
});

export default function PDFGenerator() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const idParam = params.get("id");

  const [article, setArticle] = useState(defaultArticle());
  const [savedId, setSavedId] = useState(idParam || null);
  const savedIdRef = useRef(idParam || null);
  const savePromiseRef = useRef(null);
  const setSavedIdSync = (id) => { savedIdRef.current = id; setSavedId(id); };
  const [slug, setSlug] = useState("");
  const [activeTab, setActiveTab] = useState("metadata");
  const [pdfUrl, setPdfUrl] = useState("");
  const [previewKey, setPreviewKey] = useState(0);
  const [issues, setIssues] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSub, setSelectedSub] = useState("");

  useEffect(() => { api.get("/admin/issues").then((r) => setIssues(r.data || [])); }, []);
  useEffect(() => { api.get("/admin/submissions").then((r) => setSubmissions(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (idParam) {
      api.get(`/admin/articles/${idParam}`).then((r) => {
        setArticle({ ...defaultArticle(), ...r.data, keywords: r.data.keywords || [], references: r.data.references || [] });
        setSavedIdSync(r.data.id);
        setSlug(r.data.slug);
        if (r.data.pdf_url) setPdfUrl(r.data.pdf_url);
      });
    }
  }, [idParam]);

  const set = (k, v) => setArticle((a) => ({ ...a, [k]: v }));

  const loadFromSubmission = (sid) => {
    const sub = submissions.find((s) => s.id === sid);
    if (!sub) return;
    const hasData = article.title || (article.authors || []).some((a) => a.first_name || a.last_name);
    if (hasData && !window.confirm("This will REPLACE the current form (metadata, authors, abstract, keywords and figures) with the selected submission's data. Continue?")) {
      setSelectedSub("");
      return;
    }
    // Map submission authors -> PDF author shape (keeps photo_url, orcid, etc.)
    let authors = (sub.authors || []).map((a, i) => ({
      ...blankAuthor(),
      first_name: a.first_name || "", middle_name: a.middle_name || "",
      last_name: a.last_name || "", second_last_name: a.second_last_name || "",
      academic_degree: a.academic_degree || "", email: a.email || "", orcid: a.orcid || "",
      affiliation: a.affiliation || "", department: a.department || "", institution: a.institution || "",
      city: a.city || "", country: a.country || "", address: a.address || "",
      order: a.order || i + 1, corresponding: !!a.corresponding, photo_url: a.photo_url || "",
    }));
    if (authors.length === 0) {
      authors = [{
        ...blankAuthor(), corresponding: true,
        first_name: sub.corresponding_first_name || "", middle_name: sub.corresponding_middle_name || "",
        last_name: sub.corresponding_last_name || "", second_last_name: sub.corresponding_second_last_name || "",
        email: sub.corresponding_email || "", orcid: sub.corresponding_orcid || "",
        affiliation: sub.corresponding_affiliation || "", department: sub.corresponding_department || "",
        institution: sub.corresponding_institution || "", city: sub.corresponding_city || "", country: sub.corresponding_country || "",
      }];
    }
    // Uploaded figure images -> figures
    const figures = (sub.files || [])
      .filter((f) => f.kind === "figure")
      .map((f, i) => ({ number: i + 1, title: f.filename || `Figure ${i + 1}`, legend: "", image_url: f.stored_path, place_after: "Results", include_in_pdf: true }));

    setArticle({
      ...defaultArticle(),
      title: sub.title || "",
      short_title: sub.short_title || "",
      article_type: sub.article_type || "Original Research",
      main_specialty: sub.main_specialty || "",
      structured_abstract: false,
      abstract: sub.abstract || "",
      keywords: sub.keywords || [],
      authors: authors.length ? authors : [{ ...blankAuthor(), corresponding: true }],
      figures,
    });
    setSavedIdSync(null);
    setSlug("");
    setPdfUrl("");
    setActiveTab("metadata");
    toast.success(`Loaded submission ${sub.submission_id}. Body text (Introduction/Methods/…) must be filled from the manuscript file.`);
  };

  const addAuthor = () => set("authors", [...article.authors, { ...blankAuthor(), order: article.authors.length + 1 }]);
  const removeAuthor = (i) => set("authors", article.authors.filter((_, idx) => idx !== i));
  const updateAuthor = (i, k, v) => set("authors", article.authors.map((a, idx) => idx === i ? { ...a, [k]: v } : a));

  const uploadAuthorPhoto = async (i, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateAuthor(i, "photo_url", data.stored_path);
      toast.success("Author photo uploaded");
    } catch { toast.error("Photo upload failed"); }
  };

  const addRef = () => set("references", [...article.references, ""]);
  const updateRef = (i, v) => set("references", article.references.map((r, idx) => idx === i ? v : r));
  const removeRef = (i) => set("references", article.references.filter((_, idx) => idx !== i));

  const addCustomSection = () => set("custom_sections", [...article.custom_sections, { title: "", content: "" }]);
  const updateCustomSection = (i, k, v) => set("custom_sections", article.custom_sections.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removeCustomSection = (i) => set("custom_sections", article.custom_sections.filter((_, idx) => idx !== i));

  const addTable = () => set("tables", [...article.tables, { number: article.tables.length + 1, title: "", rows: [["Header 1", "Header 2"], ["", ""]], footnotes: "", place_after: "Results", include_in_pdf: true }]);
  const updateTable = (i, k, v) => set("tables", article.tables.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  const removeTable = (i) => set("tables", article.tables.filter((_, idx) => idx !== i));
  const setTableRows = (i, rows) => set("tables", article.tables.map((t, idx) => idx === i ? { ...t, rows } : t));

  const updateTableCell = (ti, ri, ci, v) => {
    set("tables", article.tables.map((t, idx) => {
      if (idx !== ti) return t;
      const rows = t.rows.map((r, rIdx) => rIdx === ri ? r.map((c, cIdx) => cIdx === ci ? v : c) : r);
      return { ...t, rows };
    }));
  };
  const addTableRows = (ti, count = 1) => {
    set("tables", article.tables.map((t, idx) => {
      if (idx !== ti) return t;
      const ncols = (t.rows[0] || []).length || 1;
      const newRows = Array.from({ length: count }, () => Array(ncols).fill(""));
      return { ...t, rows: [...t.rows, ...newRows] };
    }));
  };
  const addTableCols = (ti, count = 1) => {
    set("tables", article.tables.map((t, idx) => {
      if (idx !== ti) return t;
      return { ...t, rows: t.rows.map((r) => [...r, ...Array(count).fill("")]) };
    }));
  };
  const delTableRow = (ti, ri) => {
    set("tables", article.tables.map((t, idx) => idx === ti ? { ...t, rows: t.rows.filter((_, r) => r !== ri) } : t));
  };
  const delTableCol = (ti, ci) => {
    set("tables", article.tables.map((t, idx) => idx === ti ? { ...t, rows: t.rows.map((r) => r.filter((_, c) => c !== ci)) } : t));
  };

  const addFigure = () => set("figures", [...article.figures, { number: article.figures.length + 1, title: "", legend: "", image_url: "", place_after: "Results", include_in_pdf: true }]);
  const updateFigure = (i, k, v) => set("figures", article.figures.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  const removeFigure = (i) => set("figures", article.figures.filter((_, idx) => idx !== i));

  const uploadFigure = async (i, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/uploads/figure", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateFigure(i, "image_url", data.stored_path);
      toast.success("Figure uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const saveArticle = async (opts = {}) => {
    // Reuse an in-flight save so concurrent clicks (e.g. Save Draft + Generate)
    // never create duplicate articles.
    if (savePromiseRef.current) {
      try {
        const data = await savePromiseRef.current;
        if (opts.thenGenerate) await generatePDF(opts.publish);
        return data;
      } catch { return; }
    }
    const doSave = (async () => {
      const currentId = savedIdRef.current;
      if (currentId) {
        const res = await api.put(`/admin/articles/${currentId}`, article);
        return res.data;
      }
      const res = await api.post("/admin/articles", article);
      setSavedIdSync(res.data.id);
      setSlug(res.data.slug);
      navigate(`/admin/pdf-generator?id=${res.data.id}`, { replace: true });
      return res.data;
    })();
    savePromiseRef.current = doSave;
    try {
      const data = await doSave;
      toast.success("Article saved as draft");
      if (opts.thenGenerate) await generatePDF(opts.publish);
      return data;
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      savePromiseRef.current = null;
    }
  };

  const generatePDF = async (publish = false) => {
    let id = savedIdRef.current;
    if (!id) {
      const a = await saveArticle();
      if (!a) return;
      id = savedIdRef.current || a.id;
    }
    try {
      const { data } = await api.post("/admin/pdf-generator/generate", { article_id: id, publish });
      setPdfUrl(data.pdf_url);
      setPreviewKey((k) => k + 1);
      toast.success(publish ? "Official PDF generated & published!" : "Official PDF generated.");
    } catch (e) {
      toast.error("PDF generation failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const previewPDF = async () => {
    try {
      const token = localStorage.getItem("csr_token");
      const res = await fetch(`${ASSET_BASE}/api/admin/pdf-generator/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(article),
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPreviewKey((k) => k + 1);
      toast.success("Live preview generated");
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const Tabs = [
    { key: "metadata", label: "Metadata" },
    { key: "authors", label: "Authors" },
    { key: "abstract", label: "Abstract" },
    { key: "main", label: "Main Text" },
    { key: "tables", label: "Tables" },
    { key: "figures", label: "Figures" },
    { key: "refs", label: "References" },
    { key: "extras", label: "Extras" },
    { key: "visual", label: "Visual Abstract" },
  ];

  return (
    <div data-testid="pdf-generator">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h1 className="font-serif text-3xl text-[#001F3F] flex items-center gap-3">
          <FileSignature className="w-7 h-7 text-[#2BA6A6]" />
          Official PDF Generator
        </h1>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => saveArticle()} className="bg-white border border-[#001F3F] text-[#001F3F] px-4 py-2 text-xs uppercase tracking-wider hover:bg-[#001F3F] hover:text-white" data-testid="save-draft-btn">
            <Save className="w-3 h-3 inline mr-1" /> Save Draft
          </button>
          <button onClick={previewPDF} className="bg-white border border-[#2BA6A6] text-[#2BA6A6] px-4 py-2 text-xs uppercase tracking-wider hover:bg-[#2BA6A6] hover:text-white" data-testid="preview-pdf-btn">
            <Eye className="w-3 h-3 inline mr-1" /> Live Preview
          </button>
          <button onClick={() => saveArticle({ thenGenerate: true, publish: false })} className="bg-[#2BA6A6] text-white px-4 py-2 text-xs uppercase tracking-wider" data-testid="generate-pdf-btn">
            Generate Official PDF
          </button>
          <button onClick={() => saveArticle({ thenGenerate: true, publish: true })} className="bg-[#001F3F] text-white px-4 py-2 text-xs uppercase tracking-wider" data-testid="publish-btn">
            Generate &amp; Publish
          </button>
        </div>
      </div>
      <div className="w-10 h-[2px] bg-[#2BA6A6] mb-2"></div>
      {slug && <div className="text-xs font-sans text-gray-500 mb-3">Permanent URL: <Link to={`/articles/${slug}`} target="_blank" className="text-[#2BA6A6]">/articles/{slug}</Link></div>}

      {/* Load from an existing manuscript submission */}
      <div className="bg-[#F5F7FA] border border-gray-200 p-4 mb-6" data-testid="load-submission-bar">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-sans uppercase tracking-[0.15em] font-semibold text-[#001F3F]">Load from Submission</span>
          <select
            value={selectedSub}
            onChange={(e) => { setSelectedSub(e.target.value); if (e.target.value) loadFromSubmission(e.target.value); }}
            className="flex-1 min-w-[260px] border border-gray-300 px-3 py-2 text-sm bg-white"
            data-testid="submission-select"
          >
            <option value="">— Select a submission to auto-fill authors & metadata —</option>
            {submissions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.submission_id} — {(s.title || "Untitled").slice(0, 60)} [{s.status || "new"}]
              </option>
            ))}
          </select>
        </div>
        {selectedSub && (() => {
          const sub = submissions.find((s) => s.id === selectedSub);
          const files = (sub?.files || []);
          return files.length ? (
            <div className="mt-3 flex flex-wrap gap-2 text-xs" data-testid="submission-files">
              <span className="text-gray-500">Uploaded files:</span>
              {files.map((f, i) => (
                <a key={i} href={downloadUrl(f.stored_path, f.filename)} className="inline-flex items-center gap-1 text-[#2BA6A6] hover:underline">
                  <Download className="w-3 h-3" /> {f.kind}: {f.filename}
                </a>
              ))}
            </div>
          ) : <div className="mt-2 text-xs text-gray-400">No files uploaded with this submission.</div>;
        })()}
        <p className="mt-2 text-[11px] text-gray-500">Auto-fills authors (with photo), title, type, specialty, abstract, keywords and figure images. Body text (Introduction/Methods/Results…) must be entered manually from the manuscript file.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200">
          <div className="border-b border-gray-200 px-4 flex gap-1 overflow-x-auto">
            {Tabs.map((t) => (
              <button key={t.key} onClick={() => setActiveTab(t.key)} className={`px-4 py-3 text-xs font-sans uppercase tracking-wider whitespace-nowrap border-b-2 ${activeTab === t.key ? "border-[#2BA6A6] text-[#2BA6A6] font-semibold" : "border-transparent text-[#001F3F]/70 hover:text-[#2BA6A6]"}`} data-testid={`tab-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="p-5 max-h-[70vh] overflow-y-auto">
            {activeTab === "metadata" && <MetadataTab article={article} set={set} issues={issues} />}
            {activeTab === "authors" && <AuthorsTab article={article} addAuthor={addAuthor} removeAuthor={removeAuthor} updateAuthor={updateAuthor} uploadAuthorPhoto={uploadAuthorPhoto} />}
            {activeTab === "abstract" && <AbstractTab article={article} set={set} />}
            {activeTab === "main" && <MainTextTab article={article} set={set} addCustomSection={addCustomSection} updateCustomSection={updateCustomSection} removeCustomSection={removeCustomSection} />}
            {activeTab === "tables" && <TablesTab article={article} addTable={addTable} updateTable={updateTable} removeTable={removeTable} updateTableCell={updateTableCell} addTableRows={addTableRows} addTableCols={addTableCols} delTableRow={delTableRow} delTableCol={delTableCol} setTableRows={setTableRows} />}
            {activeTab === "figures" && <FiguresTab article={article} addFigure={addFigure} updateFigure={updateFigure} removeFigure={removeFigure} uploadFigure={uploadFigure} />}
            {activeTab === "refs" && <ReferencesTab article={article} addRef={addRef} updateRef={updateRef} removeRef={removeRef} />}
            {activeTab === "extras" && <ExtrasTab article={article} set={set} />}
            {activeTab === "visual" && <VisualAbstractTab article={article} set={set} />}
          </div>
        </div>

        <div className="bg-[#F5F7FA] border border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="text-xs font-sans uppercase tracking-wider text-[#001F3F] font-semibold">PDF Preview</div>
            {pdfUrl && (
              <a href={pdfUrl.startsWith("blob:") ? pdfUrl : assetUrl(pdfUrl)} download className="text-xs text-[#2BA6A6] hover:text-[#001F3F] uppercase tracking-wider flex items-center gap-1">
                <Download className="w-3 h-3" /> Download
              </a>
            )}
          </div>
          <div className="flex-1 min-h-[800px]">
            {pdfUrl ? (
              <iframe key={previewKey} src={pdfUrl.startsWith("blob:") ? pdfUrl : assetUrl(pdfUrl)} className="w-full h-[800px] bg-white" title="PDF Preview" data-testid="pdf-preview-iframe"></iframe>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 font-serif p-10 text-center min-h-[400px]">
                Click "Live Preview" to render the PDF using current form data.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----- Tabs ----- */
function L({ children, required }) { return <label className="block text-[10px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-1.5">{children}{required && <span className="text-[#2BA6A6]"> *</span>}</label>; }
function T(props) { return <input {...props} className={`w-full px-3 py-2 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6] ${props.className || ""}`} />; }
function TA(props) { return <textarea {...props} className={`w-full px-3 py-2 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6] ${props.className || ""}`} />; }
function S({ children, ...rest }) { return <select {...rest} className="w-full px-3 py-2 bg-white border border-gray-200 text-sm font-sans">{children}</select>; }
function G({ children, cols = 2 }) { return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-3`}>{children}</div>; }

function MetadataTab({ article, set, issues }) {
  return (
    <div className="space-y-4">
      <div><L required>Article Title</L><T value={article.title} onChange={(e) => set("title", e.target.value)} data-testid="meta-title" /></div>
      <G>
        <div><L>Short Title</L><T value={article.short_title} onChange={(e) => set("short_title", e.target.value)} /></div>
        <div><L>Running Title</L><T value={article.running_title} onChange={(e) => set("running_title", e.target.value)} /></div>
      </G>
      <G cols={3}>
        <div><L>Article Type</L>
          <S value={article.article_type} onChange={(e) => set("article_type", e.target.value)}>
            {ARTICLE_TYPES.map((t) => <option key={t}>{t}</option>)}
          </S>
        </div>
        <ComboTextField label="Main Specialty" value={article.main_specialty} onChange={(v) => set("main_specialty", v)} options={SPECIALTIES} placeholder="Select…" />
        <div><L>License</L><T value={article.license} onChange={(e) => set("license", e.target.value)} /></div>
      </G>
      <G cols={4}>
        <div><L>Volume</L><T value={article.volume} onChange={(e) => set("volume", e.target.value)} /></div>
        <div><L>Issue</L><T value={article.issue} onChange={(e) => set("issue", e.target.value)} /></div>
        <div><L>Year</L><T value={article.year} onChange={(e) => set("year", e.target.value)} /></div>
        <div><L>Pages or Article #</L><T value={article.pages} onChange={(e) => set("pages", e.target.value)} placeholder="e.g. 101-108 or csr-2026-001" /></div>
      </G>
      <G>
        <div><L>DOI</L><T value={article.doi} onChange={(e) => set("doi", e.target.value)} /></div>
        <div><L>DOI URL</L><T value={article.doi_url} onChange={(e) => set("doi_url", e.target.value)} /></div>
      </G>
      <G cols={3}>
        <DatePickerField label="Received" value={article.received_date} onChange={(v) => set("received_date", v)} testId="date-received" />
        <DatePickerField label="Accepted" value={article.accepted_date} onChange={(v) => set("accepted_date", v)} testId="date-accepted" />
        <DatePickerField label="Published" value={article.published_date} onChange={(v) => set("published_date", v)} testId="date-published" />
      </G>
      <G>
        <div><L>Issue (Optional)</L>
          <S value={article.issue_id} onChange={(e) => {
            const id = e.target.value;
            set("issue_id", id);
            const iss = issues.find((x) => x.id === id);
            if (iss) {
              if (iss.volume !== undefined && iss.volume !== null && iss.volume !== "") set("volume", String(iss.volume));
              if (iss.issue !== undefined && iss.issue !== null && iss.issue !== "") set("issue", String(iss.issue));
              if (iss.year !== undefined && iss.year !== null && iss.year !== "") set("year", String(iss.year));
            }
          }} data-testid="pdf-issue-select">
            <option value="">— Not assigned —</option>
            {issues.map((i) => <option key={i.id} value={i.id}>Vol {i.volume}/Issue {i.issue} · {i.year}</option>)}
          </S>
          <p className="mt-1 text-[11px] text-gray-500">Selecting an issue auto-fills Volume, Issue and Year above.</p>
        </div>
        <div className="flex items-end gap-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={article.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#2BA6A6]" /> Featured</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={article.published} onChange={(e) => set("published", e.target.checked)} className="accent-[#2BA6A6]" /> Published</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={article.hidden} onChange={(e) => set("hidden", e.target.checked)} className="accent-[#2BA6A6]" /> Hidden</label>
        </div>
      </G>
      <HowToCiteField article={article} set={set} />
    </div>
  );
}

function HowToCiteField({ article, set }) {
  const auto = useMemo(() => buildCitation(article), [article]);
  // If user has not customized, mirror the auto value to keep it in sync
  useEffect(() => {
    if (!article.how_to_cite || article.how_to_cite === article._lastAutoCite) {
      set("how_to_cite", auto);
      set("_lastAutoCite", auto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto]);

  const isCustom = article.how_to_cite && article.how_to_cite !== auto;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <L>How to Cite {isCustom && <span className="text-amber-600 normal-case tracking-normal text-[10px]">(manually edited)</span>}</L>
        <button
          type="button"
          onClick={() => { set("how_to_cite", auto); set("_lastAutoCite", auto); }}
          className="text-[10px] text-[#2BA6A6] hover:text-[#001F3F] uppercase tracking-wider flex items-center gap-1"
          data-testid="auto-cite-btn"
        >
          <RefreshCw className="w-3 h-3" /> Regenerate from metadata
        </button>
      </div>
      <TA rows={2} value={article.how_to_cite || ""} onChange={(e) => set("how_to_cite", e.target.value)} data-testid="how-to-cite" />
      <div className="text-[10px] text-gray-500 mt-1">
        Auto-generated from authors, title, volume/issue, year, pages and DOI. Edit manually if needed.
      </div>
    </div>
  );
}

function AuthorsTab({ article, addAuthor, removeAuthor, updateAuthor, uploadAuthorPhoto }) {
  return (
    <div className="space-y-4">
      {article.authors.map((a, i) => (
        <div key={i} className="border border-gray-200 p-4" data-testid={`pdf-author-${i}`}>
          <div className="flex justify-between mb-3"><div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#2BA6A6]">Author {i + 1}</div><button onClick={() => removeAuthor(i)} className="text-red-500 text-xs"><Trash2 className="w-3 h-3 inline" /></button></div>
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-4 bg-[#F5F7FA] border border-gray-200 p-3">
            <div className="shrink-0">
              {a.photo_url ? (
                <img src={assetUrl(a.photo_url)} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-[#2BA6A6]" data-testid={`pdf-author-photo-${i}`} />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#001F3F]/10 border border-gray-200 flex items-center justify-center text-[10px] text-[#001F3F] text-center px-2" data-testid={`pdf-author-photo-empty-${i}`}>No photo</div>
              )}
            </div>
            <div className="flex-1">
              <L>Author Photo</L>
              <p className="text-[11px] text-gray-500 mb-2">This is the photo the author uploaded during submission (loaded automatically). It is stored with the article on <strong>Generate &amp; Publish</strong>. You can replace it below.</p>
              <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadAuthorPhoto(i, e.target.files[0])} className="text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-[#001F3F] file:text-white file:cursor-pointer hover:file:bg-[#2BA6A6]" data-testid={`pdf-author-photo-upload-${i}`} />
              {a.photo_url && (
                <button onClick={() => updateAuthor(i, "photo_url", "")} className="ml-3 text-xs text-red-500 hover:text-red-700 uppercase tracking-wider" data-testid={`pdf-author-photo-remove-${i}`}>Remove</button>
              )}
            </div>
          </div>
          <G>
            <div><L>First Name</L><T value={a.first_name} onChange={(e) => updateAuthor(i, "first_name", e.target.value)} /></div>
            <div><L>Last Name</L><T value={a.last_name} onChange={(e) => updateAuthor(i, "last_name", e.target.value)} /></div>
            <div><L>Middle Name</L><T value={a.middle_name} onChange={(e) => updateAuthor(i, "middle_name", e.target.value)} /></div>
            <div><L>Second Last Name</L><T value={a.second_last_name} onChange={(e) => updateAuthor(i, "second_last_name", e.target.value)} /></div>
            <ComboTextField label="Degree" value={a.academic_degree} onChange={(v) => updateAuthor(i, "academic_degree", v)} options={ACADEMIC_DEGREES} placeholder="MD, PhD…" />
            <div><L>Email</L><T value={a.email} onChange={(e) => updateAuthor(i, "email", e.target.value)} /></div>
            <div><L>ORCID</L><T value={a.orcid} onChange={(e) => updateAuthor(i, "orcid", e.target.value)} /></div>
            <div><L>Affiliation</L><T value={a.affiliation} onChange={(e) => updateAuthor(i, "affiliation", e.target.value)} /></div>
            <ComboTextField label="Department" value={a.department} onChange={(v) => updateAuthor(i, "department", v)} options={DEPARTMENTS} placeholder="Select…" />
            <div><L>Institution</L><T value={a.institution} onChange={(e) => updateAuthor(i, "institution", e.target.value)} /></div>
            <div><L>City</L><T value={a.city} onChange={(e) => updateAuthor(i, "city", e.target.value)} /></div>
            <div><L>Country</L><T value={a.country} onChange={(e) => updateAuthor(i, "country", e.target.value)} /></div>
            <div><L>Address</L><T value={a.address} onChange={(e) => updateAuthor(i, "address", e.target.value)} /></div>
            <div><L>Order</L><T type="number" value={a.order} onChange={(e) => updateAuthor(i, "order", parseInt(e.target.value) || 1)} /></div>
          </G>
          <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={a.corresponding} onChange={(e) => updateAuthor(i, "corresponding", e.target.checked)} className="accent-[#2BA6A6]" /> Corresponding author</label>
        </div>
      ))}
      <button onClick={addAuthor} className="text-sm font-sans uppercase tracking-wider text-[#2BA6A6] flex items-center gap-2 hover:text-[#001F3F]"><Plus className="w-4 h-4" /> Add Author</button>
    </div>
  );
}

function AbstractTab({ article, set }) {
  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={article.structured_abstract} onChange={(e) => set("structured_abstract", e.target.checked)} className="accent-[#2BA6A6]" /> Use structured abstract (Background / Methods / Results / Conclusions)</label>
      {article.structured_abstract ? (
        <>
          <div><L>Background</L><TA rows={3} value={article.abstract_background} onChange={(e) => set("abstract_background", e.target.value)} /></div>
          <div><L>Methods</L><TA rows={3} value={article.abstract_methods} onChange={(e) => set("abstract_methods", e.target.value)} /></div>
          <div><L>Results</L><TA rows={3} value={article.abstract_results} onChange={(e) => set("abstract_results", e.target.value)} /></div>
          <div><L>Conclusions</L><TA rows={3} value={article.abstract_conclusions} onChange={(e) => set("abstract_conclusions", e.target.value)} /></div>
        </>
      ) : (
        <div><L>Abstract</L><TA rows={10} value={article.abstract} onChange={(e) => set("abstract", e.target.value)} /></div>
      )}
      <div><L>Keywords (comma-separated)</L>
        <T value={(article.keywords || []).join(", ")} onChange={(e) => set("keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
      </div>
    </div>
  );
}

function MainTextTab({ article, set, addCustomSection, updateCustomSection, removeCustomSection }) {
  const atype = (article.article_type || "").toLowerCase();
  const isCase = atype.includes("case report") || atype.includes("case series");
  const isSurg = atype.includes("surgical technique");
  const fields = isCase
    ? [
        { k: "introduction", label: "Introduction" },
        { k: "case_presentation", label: "Case Presentation" },
        { k: "discussion", label: "Discussion" },
        { k: "conclusion", label: "Conclusion" },
      ]
    : isSurg
    ? [
        { k: "introduction", label: "Introduction" },
        { k: "surgical_technique", label: "Surgical Technique" },
        { k: "discussion", label: "Discussion" },
        { k: "conclusion", label: "Conclusion" },
      ]
    : [
        { k: "introduction", label: "Introduction" },
        { k: "methods", label: "Methods" },
        { k: "results", label: "Results" },
        { k: "discussion", label: "Discussion" },
        { k: "conclusion", label: "Conclusion" },
      ];
  return (
    <div className="space-y-4">
      {isCase && (
        <div className="bg-[#E8F2F4] border border-[#2BA6A6]/40 px-4 py-3 text-[12px] text-[#001F3F] font-sans" data-testid="case-report-structure-hint">
          <strong>Case Report / Case Series structure:</strong> Introduction → Case Presentation → Discussion → Conclusion.
          Title, Abstract &amp; Keywords are set in their own tabs; Informed Consent &amp; Declarations go in the Extras tab; References in the References tab.
        </div>
      )}
      {isSurg && (
        <div className="bg-[#E8F2F4] border border-[#2BA6A6]/40 px-4 py-3 text-[12px] text-[#001F3F] font-sans" data-testid="surgical-technique-structure-hint">
          <strong>Surgical Technique structure:</strong> Introduction → Surgical Technique → Discussion → Conclusion.
          Describe indications, preparation, step-by-step technique, instruments and postoperative care in the Surgical Technique section.
        </div>
      )}
      {fields.map(({ k, label }) => (
        <div key={k}>
          <L>{label}</L>
          <TA rows={6} value={article[k]} onChange={(e) => set(k, e.target.value)} />
        </div>
      ))}
      <div className="border-t border-gray-200 pt-4">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#2BA6A6] mb-3">Custom Sections</div>
        {article.custom_sections.map((s, i) => (
          <div key={i} className="border border-gray-200 p-3 mb-3">
            <div className="flex gap-2 mb-2">
              <T placeholder="Section title" value={s.title} onChange={(e) => updateCustomSection(i, "title", e.target.value)} />
              <button onClick={() => removeCustomSection(i)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
            </div>
            <TA rows={4} value={s.content} onChange={(e) => updateCustomSection(i, "content", e.target.value)} />
          </div>
        ))}
        <button onClick={addCustomSection} className="text-sm text-[#2BA6A6] flex items-center gap-1"><Plus className="w-3 h-3" /> Add custom section</button>
      </div>
    </div>
  );
}

function TablesTab({ article, addTable, updateTable, removeTable, updateTableCell, addTableRows, addTableCols, delTableRow, delTableCol, setTableRows }) {
  return (
    <div className="space-y-5">
      {article.tables.map((t, i) => (
        <TableEditor
          key={i}
          index={i}
          table={t}
          updateTable={updateTable}
          removeTable={removeTable}
          updateTableCell={updateTableCell}
          addTableRows={addTableRows}
          addTableCols={addTableCols}
          delTableRow={delTableRow}
          delTableCol={delTableCol}
          setTableRows={setTableRows}
        />
      ))}
      <button onClick={addTable} className="text-sm text-[#2BA6A6] flex items-center gap-1" data-testid="add-table"><Plus className="w-3 h-3" /> Add table</button>
    </div>
  );
}

function TableEditor({ index: i, table: t, updateTable, removeTable, updateTableCell, addTableRows, addTableCols, delTableRow, delTableCol, setTableRows }) {
  const [bulkN, setBulkN] = useState(2);
  const tableRef = useRef(null);

  const ncols = (t.rows[0] || []).length || 1;
  const widths = (Array.isArray(t.col_widths) && t.col_widths.length === ncols && t.col_widths.every((w) => typeof w === "number"))
    ? t.col_widths
    : Array(ncols).fill(100 / ncols);

  const startDrag = (ci, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const tblW = tableRef.current ? tableRef.current.offsetWidth : 600;
    const start = [...widths];
    const MIN = 6;
    const onMove = (ev) => {
      const deltaPct = ((ev.clientX - startX) / tblW) * 100;
      const left = start[ci] + deltaPct;
      const right = start[ci + 1] - deltaPct;
      if (left < MIN || right < MIN) return;
      const next = [...start];
      next[ci] = Math.round(left * 10) / 10;
      next[ci + 1] = Math.round(right * 10) / 10;
      updateTable(i, "col_widths", next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const resetWidths = () => updateTable(i, "col_widths", Array(ncols).fill(Math.round((100 / ncols) * 10) / 10));

  const onPaste = (e) => {
    const rows = parsePastedTable(e.clipboardData);
    if (!rows) return; // let default paste happen for single-cell text
    e.preventDefault();
    setTableRows(i, rows);
    toast.success(`Pasted ${rows.length}×${rows[0].length} table`);
  };

  return (
    <div className="border border-gray-200 p-4" data-testid={`table-editor-${i}`}>
      <div className="flex justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#2BA6A6]">Table {t.number}</div>
        <button onClick={() => removeTable(i)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
      </div>
      <G cols={3}>
        <div><L>Number</L><T type="number" value={t.number} onChange={(e) => updateTable(i, "number", parseInt(e.target.value) || 1)} /></div>
        <div className="md:col-span-2"><L>Title</L><T value={t.title} onChange={(e) => updateTable(i, "title", e.target.value)} /></div>
      </G>
      <div className="mt-3"><L>Place after section</L>
        <S value={t.place_after} onChange={(e) => updateTable(i, "place_after", e.target.value)}>
          <option>Introduction</option><option>Methods</option><option>Results</option><option>Case Presentation</option><option>Surgical Technique</option><option>Discussion</option><option>Conclusion</option>
        </S>
      </div>
      <div className="mt-3 bg-[#F5F7FA] border border-gray-200 p-3 text-[11px] text-gray-600 flex items-center gap-2">
        <ClipboardPaste className="w-4 h-4 text-[#2BA6A6] shrink-0" />
        <div>Tip: paste a table from Word/Excel/Google Docs (Ctrl/⌘+V). Drag the <strong>teal dividers</strong> between column headers to give some columns more width — this is exactly how the columns will be sized in the PDF, and long text now wraps to a new line instead of overlapping.</div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">Column widths (drag dividers)</div>
        <button onClick={resetWidths} className="text-[10px] uppercase tracking-wider text-[#2BA6A6] hover:text-[#001F3F]" data-testid={`reset-cols-${i}`}>Reset widths</button>
      </div>
      <div className="mt-1 overflow-x-auto" onPaste={onPaste}>
        <table ref={tableRef} className="w-full text-xs border border-gray-200 table-fixed" data-testid={`table-grid-${i}`}>
          <colgroup>
            {widths.map((w, ci) => <col key={ci} style={{ width: `${w}%` }} />)}
            <col style={{ width: "34px" }} />
          </colgroup>
          <tbody>
            {t.rows.map((row, ri) => (
              <tr key={ri} className={ri === 0 ? "bg-[#F5F7FA]" : ""}>
                {row.map((cell, ci) => (
                  <td key={ci} className="border border-gray-100 p-1 relative align-top">
                    <input value={cell} onChange={(e) => updateTableCell(i, ri, ci, e.target.value)} className="w-full px-2 py-1 text-xs bg-white border-0 focus:outline-none" />
                    {ri === 0 && ci < row.length - 1 && (
                      <div
                        onMouseDown={(e) => startDrag(ci, e)}
                        title="Drag to resize columns"
                        data-testid={`col-resizer-${i}-${ci}`}
                        className="absolute top-0 right-[-3px] w-[6px] h-full cursor-col-resize bg-[#2BA6A6]/50 hover:bg-[#2BA6A6] z-10"
                      />
                    )}
                  </td>
                ))}
                <td className="p-1 text-center">
                  <button onClick={() => delTableRow(i, ri)} className="text-red-400 text-xs" title="Delete row">×</button>
                </td>
              </tr>
            ))}
            {t.rows[0] && (
              <tr>
                {t.rows[0].map((_, ci) => (
                  <td key={ci} className="p-1 text-center">
                    <button onClick={() => delTableCol(i, ci)} className="text-red-400 text-[10px]" title="Delete column">× col</button>
                  </td>
                ))}
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-wider text-gray-500">Add</label>
            <input type="number" min={1} max={50} value={bulkN} onChange={(e) => setBulkN(Math.max(1, parseInt(e.target.value) || 1))} className="w-14 px-2 py-1 border border-gray-200 text-xs" />
            <button onClick={() => addTableRows(i, bulkN)} className="text-xs text-white bg-[#2BA6A6] px-2.5 py-1 hover:bg-[#001F3F]" data-testid={`add-rows-${i}`}>
              <Plus className="w-3 h-3 inline" /> {bulkN} {bulkN === 1 ? "row" : "rows"}
            </button>
            <button onClick={() => addTableCols(i, bulkN)} className="text-xs text-white bg-[#2BA6A6] px-2.5 py-1 hover:bg-[#001F3F]" data-testid={`add-cols-${i}`}>
              <Plus className="w-3 h-3 inline" /> {bulkN} {bulkN === 1 ? "column" : "columns"}
            </button>
          </div>
        </div>
      </div>
      <div className="mt-3"><L>Footnotes</L><TA rows={2} value={t.footnotes} onChange={(e) => updateTable(i, "footnotes", e.target.value)} /></div>
      <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={t.include_in_pdf} onChange={(e) => updateTable(i, "include_in_pdf", e.target.checked)} className="accent-[#2BA6A6]" /> Include in PDF</label>
    </div>
  );
}

function FiguresTab({ article, addFigure, updateFigure, removeFigure, uploadFigure }) {
  return (
    <div className="space-y-5">
      {article.figures.map((f, i) => (
        <div key={i} className="border border-gray-200 p-4">
          <div className="flex justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#2BA6A6]">Figure {f.number}</div>
            <button onClick={() => removeFigure(i)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
          </div>
          <G cols={3}>
            <div><L>Number</L><T type="number" value={f.number} onChange={(e) => updateFigure(i, "number", parseInt(e.target.value) || 1)} /></div>
            <div className="md:col-span-2"><L>Title</L><T value={f.title} onChange={(e) => updateFigure(i, "title", e.target.value)} /></div>
          </G>
          <div className="mt-3"><L>Legend</L><TA rows={2} value={f.legend} onChange={(e) => updateFigure(i, "legend", e.target.value)} /></div>
          <div className="mt-3"><L>Place after section</L>
            <S value={f.place_after} onChange={(e) => updateFigure(i, "place_after", e.target.value)}>
              <option>Introduction</option><option>Methods</option><option>Results</option><option>Case Presentation</option><option>Surgical Technique</option><option>Discussion</option><option>Conclusion</option>
            </S>
          </div>
          <div className="mt-3"><L>Image</L>
            <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadFigure(i, e.target.files[0])} className="text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-[#001F3F] file:text-white" />
            {f.image_url && <img src={assetUrl(f.image_url)} alt="" className="mt-2 max-h-40 border border-gray-200" />}
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.include_in_pdf} onChange={(e) => updateFigure(i, "include_in_pdf", e.target.checked)} className="accent-[#2BA6A6]" /> Include in PDF</label>
        </div>
      ))}
      <button onClick={addFigure} className="text-sm text-[#2BA6A6] flex items-center gap-1"><Plus className="w-3 h-3" /> Add figure</button>
    </div>
  );
}

function ReferencesTab({ article, addRef, updateRef, removeRef }) {
  return (
    <div className="space-y-3">
      {article.references.map((r, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="text-xs text-gray-500 w-6 pt-2">{i + 1}.</div>
          <TA rows={2} value={r} onChange={(e) => updateRef(i, e.target.value)} />
          <button onClick={() => removeRef(i)} className="text-red-500 pt-2"><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
      <button onClick={addRef} className="text-sm text-[#2BA6A6] flex items-center gap-1"><Plus className="w-3 h-3" /> Add reference</button>
    </div>
  );
}

function ExtrasTab({ article, set }) {
  return (
    <div className="space-y-4">
      {["acknowledgments", "funding", "conflicts_of_interest", "informed_consent", "data_availability", "author_contributions"].map((k) => (
        <div key={k}><L>{k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</L>
          <TA rows={3} value={article[k]} onChange={(e) => set(k, e.target.value)} />
        </div>
      ))}
    </div>
  );
}

function VisualAbstractTab({ article, set }) {
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/uploads/figure", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("visual_abstract_url", data.stored_path);
      toast.success("Visual abstract uploaded");
    } catch { toast.error("Upload failed"); }
  };
  return (
    <div className="space-y-4" data-testid="visual-abstract-tab">
      <div className="bg-[#F5F7FA] border border-gray-200 p-4 text-sm text-gray-700">
        Upload a single image that summarizes the article visually. It will appear next to the article
        in the journal home/article list (with a click-to-zoom preview) once you click <strong>Generate &amp; Publish</strong>.
        The visual abstract is <em>not</em> embedded into the PDF — it is shown only on the website.
      </div>
      <div>
        <L>Visual Abstract Image</L>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          className="text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-[#001F3F] file:text-white file:cursor-pointer hover:file:bg-[#2BA6A6]"
          data-testid="visual-abstract-upload"
        />
        {article.visual_abstract_url && (
          <div className="mt-4">
            <img src={assetUrl(article.visual_abstract_url)} alt="Visual abstract" className="max-h-72 border border-gray-200 bg-white p-2" data-testid="visual-abstract-preview" />
            <button
              onClick={() => set("visual_abstract_url", "")}
              className="mt-2 text-xs text-red-500 hover:text-red-700 uppercase tracking-wider"
            >
              Remove image
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
